import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { ActivityInterface, SleepWaitConditionsInterface, WorkflowStepInterface } from '@vendasta/automata';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { CommonDisplayActionService } from '../../common/common-display-action.service';
import { CommonDisplayActivityService } from '../../common/common-display-activity.service';
import { localize } from '../../common/controls/clock-hours.pipe';
import { getLargestTimeUnit, TimeUnit, toTimeUnit } from '../../common/controls/delay-control/delay-control.component';

function hasTimeRange(sleepWaitConditions: SleepWaitConditionsInterface | undefined) {
  if ((sleepWaitConditions?.timeRangeEndHour || 0) > 0) {
    return true;
  }
  if ((sleepWaitConditions?.timeRangeEndMinute || 0) > 0) {
    return true;
  }
  return false;
}

function titleCase(name: string) {
  return name[0].toUpperCase() + name.slice(1, name.length);
}

function handleEOM(number: number) {
  if (number === 32) {
    return 'the last day of the month';
  }
  return number;
}

/**
 * <h1> IMPORTANT </h1>
 *
 * Do not implement new actions, triggers, and filters using {@link CommonDisplayActionService}
 * nor {@link CommonDisplayActivityService}. These classes are deprecated, retained for backward
 * compatibility only, and <strong> are likely to be removed in the future. </strong>
 * Instead, subclass from {@link AutomationDisplayService} as this implements all modern interfaces,
 * provides convenience methods to make code more concise and readable,
 * and provides adapters to the old system while it is being phased out.
 * @see AutomationDisplayService
 * @example AddTagService
 */
@Injectable({ providedIn: 'root' })
export class DelayService implements CommonDisplayActivityService, CommonDisplayActionService {
  constructor(
    @Inject(TranslateService)
    private translateService: {
      stream: (keys: string | Array<string>, interpolateParams?: object) => Observable<string | any>;
    },
    @Inject(DomSanitizer) private sanitizer: any,
    @Inject(LOCALE_ID) private locale: string,
  ) {}

  getActivitySubtitle(_namespace: string, _activity: ActivityInterface): Observable<string> {
    return of('');
  }

  getActivityTitle(_namespace: string, _activity: ActivityInterface): Observable<string> {
    return this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.DELAY.ACTIVITY.STARTED_DELAY');
  }

  getTemplateActionSubtitle(namespace: string, step: WorkflowStepInterface): Observable<string> {
    return this.getActionSubtitle(namespace, step);
  }

  getActionSubtitle(_namespace: string, step: WorkflowStepInterface): Observable<string> {
    const nanos = step?.sleep || 0;
    const timeUnit = getLargestTimeUnit(nanos);
    const sleepInTimeUnit = toTimeUnit(nanos, timeUnit);
    let txKeys: string[] = [];
    let params = {};

    // Return text appropriate to the current time unit, defaulting to hours if a value is missed (e.g. new time unit added)
    // Nanos are assumed never to be used.
    if (sleepInTimeUnit > 0) {
      const keys: { [timeUnit: number]: string } = {};
      if (sleepInTimeUnit === 1) {
        keys[TimeUnit.Minute] = 'AUTOMATIONS.EDITOR.TASKS.DELAY.TIME_PERIOD.WAIT_ONE_MINUTE';
        keys[TimeUnit.Hour] = 'AUTOMATIONS.EDITOR.TASKS.DELAY.TIME_PERIOD.WAIT_ONE_HOUR';
        keys[TimeUnit.Day] = 'AUTOMATIONS.EDITOR.TASKS.DELAY.TIME_PERIOD.WAIT_ONE_DAY';
      } else {
        keys[TimeUnit.Minute] = 'AUTOMATIONS.EDITOR.TASKS.DELAY.TIME_PERIOD.WAIT_MINUTES';
        keys[TimeUnit.Hour] = 'AUTOMATIONS.EDITOR.TASKS.DELAY.TIME_PERIOD.WAIT_HOURS';
        keys[TimeUnit.Day] = 'AUTOMATIONS.EDITOR.TASKS.DELAY.TIME_PERIOD.WAIT_DAYS';
        params = { time: sleepInTimeUnit };
      }
      txKeys = [keys[timeUnit] || keys[TimeUnit.Hour]];
    }

    const swc = step.sleepWaitConditions;
    const weekdayCount = swc?.weekdaySelection?.length || 0;
    const weekdays = swc!.weekdaySelection || [];
    const daysOfMonth = swc!.dayOfMonthSelection || [];

    if (daysOfMonth.length == 1) {
      if (txKeys.length > 0) {
        txKeys = [...txKeys, 'AUTOMATIONS.EDITOR.TASKS.DELAY.TIME_PERIOD.THEN_WAIT_DAY_OF_MONTH'];
      } else {
        txKeys = [...txKeys, 'AUTOMATIONS.EDITOR.TASKS.DELAY.TIME_PERIOD.WAIT_DAY_OF_MONTH'];
      }
      params = {
        ...params,
        day: daysOfMonth[0],
      };
    } else if (daysOfMonth.length > 1) {
      if (txKeys.length > 0) {
        txKeys = [...txKeys, 'AUTOMATIONS.EDITOR.TASKS.DELAY.TIME_PERIOD.THEN_WAIT_DAYS_OF_MONTH'];
      } else {
        txKeys = [...txKeys, 'AUTOMATIONS.EDITOR.TASKS.DELAY.TIME_PERIOD.WAIT_DAYS_OF_MONTH'];
      }
      params = {
        ...params,
        first_days: daysOfMonth.slice(0, daysOfMonth.length - 1).join(', '),
        last_day: handleEOM(daysOfMonth[daysOfMonth.length - 1]),
      };
    }

    if (weekdayCount == 1) {
      if (txKeys.length > 0) {
        txKeys = [...txKeys, 'AUTOMATIONS.EDITOR.TASKS.DELAY.TIME_PERIOD.THEN_WAIT_WEEKDAY'];
      } else {
        txKeys = [...txKeys, 'AUTOMATIONS.EDITOR.TASKS.DELAY.TIME_PERIOD.WAIT_WEEKDAY'];
      }
      params = {
        ...params,
        weekday: titleCase(weekdays[0]),
      };
    } else if (weekdayCount > 1) {
      if (txKeys.length > 0) {
        txKeys = [...txKeys, 'AUTOMATIONS.EDITOR.TASKS.DELAY.TIME_PERIOD.THEN_WAIT_WEEKDAYS'];
      } else {
        txKeys = [...txKeys, 'AUTOMATIONS.EDITOR.TASKS.DELAY.TIME_PERIOD.WAIT_WEEKDAYS'];
      }
      params = {
        ...params,
        first_weekdays: weekdays
          .slice(0, weekdays.length - 1)
          .map(titleCase)
          .join(', '),
        last_weekday: titleCase(weekdays[weekdays.length - 1]),
      };
    }

    if (hasTimeRange(swc)) {
      if (txKeys.length > 0) {
        txKeys = [...txKeys, 'AUTOMATIONS.EDITOR.TASKS.DELAY.TIME_PERIOD.THEN_WAIT_TIME_RANGE'];
      } else {
        txKeys = [...txKeys, 'AUTOMATIONS.EDITOR.TASKS.DELAY.TIME_PERIOD.WAIT_TIME_RANGE'];
      }
      params = {
        ...params,
        start_range: localize(swc?.timeRangeStartHour || 0, swc?.timeRangeStartMinute || 0, this.locale) || '???',
        end_range: localize(swc?.timeRangeEndHour || 0, swc?.timeRangeEndMinute || 0, this.locale) || '???',
      };
    }

    return this.translateService.stream(txKeys, params).pipe(map((outs) => Object.values(outs).join('. ') + '.'));
  }

  getActionSubtitleSuccessState(_data: any): Observable<string> {
    return of('');
  }

  getActionSubtitleErrorState(_data: any, _step: WorkflowStepInterface): Observable<string> {
    return of('');
  }

  getActionSubtitleEmptyState(_step: WorkflowStepInterface): Observable<string> {
    return of('');
  }
}
